import { Suspense, lazy } from 'react'
import type { ComponentType, ReactElement } from 'react'
import { Navigate, createBrowserRouter } from 'react-router-dom'
import type { RouteObject } from 'react-router-dom'

import { AppShell } from './shell/AppShell'
import { ProtectedRoute, SuperadminRoute } from './shell/ProtectedRoute'
import { NotFound, RouteErrorBoundary, RouteFallback } from './shell/RouteFallback'

import Login from './routes/auth/login'
import Signup from './routes/auth/signup'
import Forgot from './routes/auth/forgot'
import Reset from './routes/auth/reset'

/**
 * ── The route table ────────────────────────────────────────────────────────────
 *
 * Every wave-2 screen is declared here already, against the exact file paths listed in
 * docs/FILE_OWNERSHIP.md. Those files land later; this table does not change when they do.
 *
 * The contract each wave-2 file must honour:
 *   • it lives at exactly the path imported below;
 *   • it has a **default export** that is a React component taking no required props;
 *   • a layout file additionally renders `<Outlet />`.
 *
 * Nothing else in this file is a wave-2 concern — routes are never registered from a feature.
 */

const lazyElement = (loader: () => Promise<{ default: ComponentType }>): ReactElement => {
  const Screen = lazy(loader)
  return (
    <Suspense fallback={<RouteFallback />}>
      <Screen />
    </Suspense>
  )
}

/* ── marketing — B1 · frontend/src/routes/marketing/** ─────────────────────── */

const marketingRoutes: RouteObject = {
  path: '/',
  element: lazyElement(() => import('./routes/marketing/layout')),
  errorElement: <RouteErrorBoundary />,
  children: [
    { index: true, element: lazyElement(() => import('./routes/marketing/home')) },
    { path: 'pricing', element: lazyElement(() => import('./routes/marketing/pricing')) },
    { path: 'whitelabel', element: lazyElement(() => import('./routes/marketing/whitelabel')) },
    { path: 'privacy', element: lazyElement(() => import('./routes/marketing/privacy')) },
    { path: 'terms', element: lazyElement(() => import('./routes/marketing/terms')) },

    /* The free tools. Anonymous, like every route in this group — but two of them *write*
     * something, so read `modules/leadtools/routes.ts` before assuming they are as passive as the
     * pages beside them. They live here rather than under `routes/public/**` because a lead magnet
     * whose reader is meant to go on and look at the product needs the header, the footer and,
     * above all, this layout's `hreflang` alternates: an acquisition page that is not indexable in
     * both languages is an acquisition page with one half switched off.
     *
     * `tools` is the index the footer and the home page link to; the three below are the tools
     * themselves. `tools/caption-length` is the only one with no back end at all — its whole
     * answer is `CAPTION_LIMITS` arithmetic, computed in the browser as somebody types. */
    { path: 'tools', element: lazyElement(() => import('./routes/marketing/tools')) },
    {
      path: 'tools/best-time-to-post',
      element: lazyElement(() => import('./routes/marketing/bestTimeToPost')),
    },
    {
      path: 'tools/caption-length',
      element: lazyElement(() => import('./routes/marketing/captionLength')),
    },
    {
      path: 'tools/hashtag-generator',
      element: lazyElement(() => import('./routes/marketing/hashtagGenerator')),
    },
  ],
}

/* ── app — B2 · B3 · B4 · frontend/src/routes/app/*.tsx ────────────────────── */

const appRoutes: RouteObject = {
  path: '/app',
  element: <ProtectedRoute />,
  errorElement: <RouteErrorBoundary />,
  children: [
    {
      element: <AppShell />,
      children: [
        { index: true, element: <Navigate to="/app/dashboard" replace /> },

        // B2 — dashboard + analytics
        { path: 'dashboard', element: lazyElement(() => import('./routes/app/dashboard')) },
        { path: 'analytics', element: lazyElement(() => import('./routes/app/analytics')) },
        { path: 'analytics/*', element: lazyElement(() => import('./routes/app/analytics')) },

        // B5 — reports. The whole configuration is in the query string, so one path serves
        // every report and a saved link re-runs against fresh data.
        { path: 'reports', element: lazyElement(() => import('./routes/app/reports')) },

        // Studio — boards a person arranges out of the analytics panels. Sibling of analytics
        // rather than a tab under it: a board is a workspace object and can hold widgets on
        // several brands at once, which no screen under the brand switcher can express. Only the
        // board id is in the URL (`?board=…`); the arrangement is a row. See features/studio/.
        { path: 'studio', element: lazyElement(() => import('./routes/app/studio')) },

        // Performance dashboards — the third thing, beside Studio and Reports and not a skin of
        // either: one brand, one window, a fixed running order of sections, and a link that renders
        // it live for a client with no account. Brand-scoped, so the top bar's brand switcher
        // selects it; `?dashboard=…` selects which one. See features/performance/.
        { path: 'performance', element: lazyElement(() => import('./routes/app/performance')) },

        // Campaigns — a named set of one brand's posts, and their combined numbers. Brand-scoped,
        // unlike Studio: membership is a column on the posts themselves. `?campaign=…` selects one
        // and `?hashtag=…` opens the grouping dialog, which is how the Analytics hashtag table
        // hands a tag over. See features/campaigns/.
        { path: 'campaigns', element: lazyElement(() => import('./routes/app/campaigns')) },

        // B3 — planner + inbox
        { path: 'planner', element: lazyElement(() => import('./routes/app/planner')) },
        { path: 'planner/*', element: lazyElement(() => import('./routes/app/planner')) },
        { path: 'inbox', element: lazyElement(() => import('./routes/app/inbox')) },
        { path: 'inbox/*', element: lazyElement(() => import('./routes/app/inbox')) },

        // B4 — brands, settings (incl. the white-label panel), billing
        { path: 'brands', element: lazyElement(() => import('./routes/app/brands')) },
        { path: 'brands/*', element: lazyElement(() => import('./routes/app/brands')) },

        // P4 — the connect flow and sync health. `/api/v1/connections/callback` redirects the
        // browser back here, so the path is part of the OAuth contract, not just navigation.
        { path: 'connections', element: lazyElement(() => import('./routes/app/connections')) },

        // The networks with no browser redirect. `startConnect` sends the browser here instead
        // of out to a consent page, so this path is part of the connect contract just as the
        // callback's is — see features/connections/CredentialConnect.tsx.
        {
          path: 'connect/:provider',
          element: lazyElement(() => import('./routes/app/connect')),
        },

        // Competitor tracking, on the two networks that publish another account's public
        // numbers to an authenticated third party. See features/competitors/.
        { path: 'competitors', element: lazyElement(() => import('./routes/app/competitors')) },

        // Automazioni — a brand's standing rules over the social inbox and the content
        // lifecycle. Top-level, like Ads: it writes on its own standing authority, unattended,
        // which is a different kind of screen from anything read-only under Analytics.
        { path: 'automations', element: lazyElement(() => import('./routes/app/automations')) },

        // Ads — Meta campaign reporting *and* campaign management. Top-level rather than a tab
        // under Analytics: everything in Analytics is read-only history, and this screen writes
        // things that cost money. See features/ads/AdsScreen.tsx.
        { path: 'ads', element: lazyElement(() => import('./routes/app/ads')) },

        // SmartLinks — the builder. The pages it publishes are served by `/s/:slug` below,
        // which is outside `/app` and outside ProtectedRoute on purpose.
        { path: 'smartlinks', element: lazyElement(() => import('./routes/app/smartlinks')) },
        { path: 'settings', element: lazyElement(() => import('./routes/app/settings')) },
        // `/app/settings/whitelabel`, `/app/settings/team`, … are handled inside settings.tsx
        { path: 'settings/*', element: lazyElement(() => import('./routes/app/settings')) },
        { path: 'billing', element: lazyElement(() => import('./routes/app/billing')) },
        { path: 'billing/*', element: lazyElement(() => import('./routes/app/billing')) },
        { path: 'tours', element: lazyElement(() => import('./routes/app/tours')) },

        // B4 — superadmin console, behind the superadmin guard
        {
          path: 'admin',
          element: <SuperadminRoute />,
          children: [
            { index: true, element: lazyElement(() => import('./routes/app/admin')) },
            { path: '*', element: lazyElement(() => import('./routes/app/admin')) },
          ],
        },
      ],
    },
  ],
}

/* ── auth + system — A4 ────────────────────────────────────────────────────── */

const routes: RouteObject[] = [
  marketingRoutes,

  { path: '/login', element: <Login />, errorElement: <RouteErrorBoundary /> },
  { path: '/signup', element: <Signup />, errorElement: <RouteErrorBoundary /> },
  { path: '/forgot', element: <Forgot />, errorElement: <RouteErrorBoundary /> },
  { path: '/reset', element: <Reset />, errorElement: <RouteErrorBoundary /> },

  // The design system reference. Readable without an account so any agent can open it.
  {
    path: '/system',
    element: lazyElement(() => import('./routes/system/index')),
    errorElement: <RouteErrorBoundary />,
  },

  /* ── public — frontend/src/routes/public/** ─────────────────────────────────
   *
   * A published SmartLink's microsite. Top level, with no `ProtectedRoute` and no `AppShell`
   * above it: its entire audience is people who have never signed in and never will. The API
   * serves the SPA for every non-`/api` path on the same origin (docs/DEPLOY.md § "One image,
   * one process"), so this path needs nothing on the server beyond the public endpoint it
   * fetches — see the route file for what that costs and what it does not. */
  {
    path: '/s/:slug',
    element: lazyElement(() => import('./routes/public/smartlink')),
    errorElement: <RouteErrorBoundary />,
  },

  /* A brand's client auto-login link. Anonymous by necessity — getting a session *is* the
   * request — and deliberately parameterless: the token rides in the URL fragment, which no
   * server ever receives. See the route file. */
  {
    path: '/autoin',
    element: lazyElement(() => import('./routes/public/autologin')),
    errorElement: <RouteErrorBoundary />,
  },

  /* A compat caller's connect-bridge link — see backend/src/compat/connectBridge.ts. Same
   * anonymous-by-necessity, parameterless-path reasoning as `/autoin` just above. */
  {
    path: '/connect-bridge',
    element: lazyElement(() => import('./routes/public/connectBridge')),
    errorElement: <RouteErrorBoundary />,
  },

  /* The far side of a real OAuth round trip for the connect-bridge flow — see
   * routes/public/connectBridgeFinish.tsx for why this one is a query string, not a fragment. */
  {
    path: '/connect-bridge-finish',
    element: lazyElement(() => import('./routes/public/connectBridgeFinish')),
    errorElement: <RouteErrorBoundary />,
  },

  /* A brand's review portal, for a client who is not on the team and never will be. Anonymous by
   * design and parameterless for the same reason as `/autoin`: the token rides in the URL
   * fragment, which no server ever receives. See the route file. */
  {
    path: '/review',
    element: lazyElement(() => import('./routes/public/review')),
    errorElement: <RouteErrorBoundary />,
  },

  /* A shared performance dashboard, for a client who is not on the team and never will be. Read
   * only, anonymous by design, and parameterless for the same reason as `/autoin` and `/review`:
   * the token rides in the URL fragment, which no server ever receives. See the route file. */
  {
    path: '/performance',
    element: lazyElement(() => import('./routes/public/performance')),
    errorElement: <RouteErrorBoundary />,
  },

  appRoutes,

  { path: '*', element: <NotFound /> },
]

export const router = createBrowserRouter(routes)
